/**
 * Canvas / viewport store — brief §7.
 */
import { zoomIdentity, type ZoomTransform } from 'd3-zoom';
import { rotationStepRadians } from '@/patterns/wallpaper';
import { usePatternStore } from '@/stores/pattern';
import { defineStore } from 'pinia';
import { ref, shallowRef, watch } from 'vue';

export type PointerKind = 'mouse' | 'touch' | 'pen';

/** Master switch for the two-finger rotate GESTURE specifically (brief 08 §1).
 *  Disabled per brief 09-layout: "Two-finger rotate is causing issues with the
 *  drag and zoom centre. Disable it and replace with a button" — rotation
 *  itself (state, easing, reset) is untouched and still driven by the
 *  Tessellation panel's rotate CW/CCW buttons (`canvasStore.rotateBy`) on both
 *  desktop and mobile. */
export const CANVAS_ROTATION_ENABLED = false;

/** Feature 1 — canvas rotation is a pure view transform, kept as a separate
 *  ref rather than folded into `viewTransform` because d3-zoom's ZoomTransform
 *  is translate+scale only (no rotation term) — see TileCanvas.vue's
 *  setPlacement() and useWrapTransform.ts for where the two combine. */
let resetAnim = 0;

/**
 * Snap `angle` to the nearest multiple of `step`, advancing by one further
 * step in `direction` ONLY if `angle` was already exactly on that grid
 * (brief 11-layout2 §1 "guard against": never blindly add a step to a
 * possibly off-grid angle — e.g. switching symmetry group mid-rotation,
 * square 45° → hex, must re-snap to the new grid, not land on 45+60=105°,
 * which is on neither). `direction: 0` is a pure re-snap with no step, used
 * on a group switch; ±1 is a normal rotate-button press.
 */
export function snappedAngle(angle: number, step: number, direction: -1 | 0 | 1): number {
  const onGrid = Math.round(angle / step) * step;
  const wasOnGrid = Math.abs(angle - onGrid) < 1e-6;
  return wasOnGrid ? onGrid + direction * step : onGrid;
}

export const useCanvasStore = defineStore('canvas', () => {
  const viewTransform = shallowRef<ZoomTransform>(zoomIdentity);
  const canvasWidth = ref(0);
  const canvasHeight = ref(0);
  /** screen px of active tile top-left */
  const tileOrigin = ref<[number, number]>([0, 0]);
  const pressureSupported = ref(false);
  const pointerType = ref<PointerKind>('mouse');
  /** radians, about the viewport centre; never touches artwork/symmetry state */
  const rotation = ref(0);
  /** true once the user has panned/zoomed away from the initial fit (set by the
   *  d3 zoom handler); drives the reset-view button's visibility. */
  const viewMoved = ref(false);
  /** bumped by `resetView` so TileCanvas can restore the d3 zoom to its fit. */
  const resetViewNonce = ref(0);

  /** Reset the whole view — pan, zoom and rotation (brief 08 §1). Rotation
   *  eases back; the pan/zoom reset is done by TileCanvas (it owns the d3 zoom
   *  behaviour + initial fit) in response to the nonce bump. */
  function resetView(): void {
    resetRotation();
    viewMoved.value = false;
    resetViewNonce.value++;
  }

  /** "Snap back to 0°" affordance — eased, not an instant jump. */
  function resetRotation(durationMs = 220): void {
    animateRotationTo(0, durationMs);
  }

  /** Ease `rotation` from wherever it is now to `to` — shared by rotateBy and
   *  resnapRotation. No-op (and no animation started) if already there. */
  function animateRotationTo(to: number, durationMs: number): void {
    cancelAnimationFrame(resetAnim);
    const from = rotation.value;
    if (from === to) return;
    const start = performance.now();
    const step = (): void => {
      const t = Math.min(1, (performance.now() - start) / durationMs);
      const eased = 1 - (1 - t) * (1 - t); // ease-out
      rotation.value = from + (to - from) * eased;
      if (t < 1) resetAnim = requestAnimationFrame(step);
      else rotation.value = to;
    };
    resetAnim = requestAnimationFrame(step);
  }

  /** Step the view rotation by one symmetry-snap unit — the Tessellation
   *  panel's rotate CW/CCW buttons (brief 09-layout), replacing the two-finger
   *  touch gesture. `step` is the current group's snap unit (radians,
   *  `rotationStepRadians`); only actually advances if the rotation is
   *  already on that grid, else it re-snaps instead (see `snappedAngle`).
   *  Eased, not an instant jump; cancels any animation already in flight and
   *  restarts cleanly from wherever rotation currently is, so rapid taps stay
   *  responsive. */
  function rotateBy(step: number, direction: -1 | 1, durationMs = 180): void {
    animateRotationTo(snappedAngle(rotation.value, step, direction), durationMs);
  }

  /** Re-snap the current rotation to a NEW grid without also stepping (brief
   *  11-layout2 §1) — called whenever the active pattern changes mid-
   *  rotation (wallpaper group, artist tile, or lattice params — any of
   *  which can change the drawing tile's shape, and the snap step is a
   *  function of that shape, not the group id alone). */
  function resnapRotation(step: number, durationMs = 180): void {
    animateRotationTo(snappedAngle(rotation.value, step, 0), durationMs);
  }

  watch(
    () => usePatternStore().currentConfig,
    () => resnapRotation(rotationStepRadians(usePatternStore().currentPattern.polygon())),
  );

  return {
    viewTransform,
    canvasWidth,
    canvasHeight,
    tileOrigin,
    pressureSupported,
    pointerType,
    rotation,
    viewMoved,
    resetViewNonce,
    resetView,
    resetRotation,
    rotateBy,
  };
});
